// importJoin.js — pure, testable logic for the multi-file bulk importer.
//
// Operators export TWO files from their old CRM and we join them the way a human
// would: by street address. Neither file alone is enough —
//   • Client List  → contact (name, email, phone, address, SMS consent)
//   • Subscriptions → plan, encoded as "Regular Plan - 2d-1xW" (dogs + cadence)
//                     and "…Deodorizing Service" lines (deo cadence)
// A single pre-combined CSV (with Dogs/Frequency columns) still works — the join
// just has nothing to add. No dispatch file — tech assignment is done in-app after.

import { toISODate } from "./date";

// ── header + value helpers ──────────────────────────────────────────────────
export const nh = (h) => (h || "").toLowerCase().replace(/\s+/g, " ").trim();

export function cleanPhone(p) {
  if (!p || p === "No data") return "";
  const d = p.replace(/\D/g, "");
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === "1") return `${d.slice(1, 4)}-${d.slice(4, 7)}-${d.slice(7)}`;
  return p;
}

export function combineName(first, last) {
  first = (first || "").trim(); last = (last || "").trim();
  const cx = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  // drop Last when it merely doubles the tail of First ("Mike Di Domizio"+"DiDomizio")
  if (!last || (cx(first) && cx(first).endsWith(cx(last)))) return first.replace(/\s+/g, " ");
  return `${first} ${last}`.replace(/\s+/g, " ").trim();
}

// Split a cleaned display name into first/last that reconstruct it exactly (the
// maintain_client_name DB trigger derives `name` from these).
export function splitName(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: parts[0] || "", last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
}

const STATE_MAP = { pennsylvania: "PA", "new jersey": "NJ", delaware: "DE", maryland: "MD", "new york": "NY" };
export function normState(s) {
  s = (s || "").trim(); if (!s) return "";
  if (s.includes("-")) s = s.split("-").pop().trim();       // "Pennsylvania - PA" → "PA"
  return STATE_MAP[s.toLowerCase()] || (s.length <= 3 ? s.toUpperCase() : s);
}

// Free-text cadence (for combined files that spell it out).
export function normFreq(s) {
  const t = nh(s); if (!t) return "";
  if (/bi.?weekly|every other week/.test(t)) return "biweekly";
  if (/two times a week|twice.?a.?week|twice.?weekly|2 ?x/.test(t)) return "twice-weekly";
  if (/once a week|(^|[^-])weekly/.test(t)) return "weekly";
  return "";
}
export function normDeo(s) {
  const t = nh(s); if (!t) return "";
  if (t.includes("month")) return "monthly";
  if (/bi.?weekly/.test(t)) return "biweekly";
  if (t.includes("weekly")) return "weekly";
  return "";
}
const firstToken = (s) => (s || "").trim().split(/\s+/)[0] || "";

// Join key: the street portion of an address, case/space-insensitive. The Client
// List stores just the street; the Subscriptions "Address" is "street, city, ST zip"
// — so both reduce to the same key.
export function normAddr(a) {
  return (a || "").split(",")[0].trim().toLowerCase().replace(/\s+/g, " ");
}

// ── plan-code decode ("Regular Plan - 2d-1xW" → 2 dogs, weekly) ──────────────
const CODE_FREQ = { "1xw": "weekly", "2xw": "twice-weekly", bw: "biweekly" };
export function parsePlanCode(subsName) {
  const m = /regular plan\s*-\s*(\d+)\s*d\s*-\s*([a-z0-9]+)/i.exec(subsName || "");
  if (!m) return null;
  return { dogs: parseInt(m[1], 10), frequency: CODE_FREQ[m[2].toLowerCase()] || "" };
}
export function deoFromSubsName(subsName) {
  const t = nh(subsName);
  if (!t.includes("deodoriz")) return "";
  if (t.includes("month")) return "monthly";
  if (/bi.?weekly/.test(t)) return "biweekly";
  if (t.includes("weekly")) return "weekly";
  return "";
}

// ── CSV parsing ──────────────────────────────────────────────────────────────
export function parseLine(line) {
  const f = []; let cur = "", inQ = false;
  for (const ch of line) {
    if (ch === '"') inQ = !inQ;
    else if (ch === "," && !inQ) { f.push(cur); cur = ""; }
    else cur += ch;
  }
  f.push(cur);
  return f.map((x) => x.replace(/"/g, "").trim());
}

function headerIndex(headerLine) {
  const idx = {}; parseLine(headerLine).forEach((h, i) => { idx[nh(h)] = i; });
  return idx;
}

// contacts vs subscriptions vs a combined/unknown file — by which headers exist.
export function detectFileType(text) {
  const idx = headerIndex((text || "").replace(/\r/g, "").split("\n")[0] || "");
  if ("subs. name" in idx || "subs name" in idx || "subs.name" in idx) return "subscriptions";
  if ("email" in idx || "first name" in idx || "cell phone number" in idx || "client name" in idx) return "contacts";
  return "unknown";
}

// Contact rows (also handles a pre-combined file that already carries Dogs/Frequency).
export function parseContactCSV(text) {
  const lines = (text || "").replace(/\r/g, "").trim().split("\n");
  const idx = headerIndex(lines[0]);
  const pick = (f, ...aliases) => {
    for (const a of aliases) {
      const i = idx[nh(a)];
      if (i != null) { const v = (f[i] || "").trim(); if (v && v.toLowerCase() !== "no data") return v; }
    }
    return "";
  };
  return lines.slice(1).map((line) => {
    const f = parseLine(line);
    const dogsRaw = pick(f, "Dogs", "Number Of Dogs").replace(/\D/g, "");
    const name = pick(f, "Name") || combineName(pick(f, "First Name"), pick(f, "Last Name")) || "Unknown";
    const { first, last } = splitName(name);
    const address = pick(f, "Address");
    return {
      name, first_name: first, last_name: last,
      email: pick(f, "Email"),
      phone: cleanPhone(pick(f, "Phone", "Cell Phone Number", "Home Phone Number")),
      address, city: pick(f, "City"), state: normState(pick(f, "State")),
      zip: pick(f, "Zip Code", "Zip"),
      frequency: normFreq(pick(f, "Frequency", "Cleanup Frequency")),
      dogs: dogsRaw ? parseInt(dogsRaw, 10) : null,
      tech: firstToken(pick(f, "Technician", "Assigned To", "Cleanup Assign To")),
      serviceDay: pick(f, "Service Day", "Service Days"),
      deoFreq: normDeo(pick(f, "Deodorizer Frequency", "Deodorizer")),
      // The one field that pins a bi-weekly client's fortnight. Their start date is
      // useless on migration (they've been served for months) and guessing is wrong
      // half the time, so we ask the old system for one real visit date instead.
      lastServiceDate: toISODate(pick(f, "Last Service Date", "Last Service", "Last Serviced",
                                      "Last Visit", "Last Cleanup", "Last Completed", "Last Completed Date")),
      // Same idea for the deodorizer, which needs its OWN date: a monthly deo has four
      // possible phases (which week of the month), so guessing it is wrong 75% of the time.
      lastDeoDate: toISODate(pick(f, "Last Deodorizer Date", "Last Deodorizer", "Last Deo",
                                  "Last Deodorizing", "Last Deodorizing Service")),
      smsConsent: /^y/i.test(pick(f, "SMS Consent", "SMS Messaging Consent")),
      _addr: normAddr(address),
    };
  }).filter((r) => r.name && r.name !== "Unknown");
}

// Subscription lines → {byAddr: {addr: {dogs, frequency, deoFreq}}}. Aggregates the
// Regular Plan line (dogs+cadence) and any Deodorizing line per address.
export function parseSubsCSV(text) {
  const lines = (text || "").replace(/\r/g, "").trim().split("\n");
  const idx = headerIndex(lines[0]);
  const iAddr = idx["address"];
  const iName = idx["subs. name"] ?? idx["subs name"] ?? idx["subs.name"];
  const byAddr = {}; let count = 0;
  for (const line of lines.slice(1)) {
    const f = parseLine(line);
    const addr = normAddr((iAddr != null ? f[iAddr] : "") || "");
    const subsName = ((iName != null ? f[iName] : "") || "").trim();
    if (!addr || !subsName) continue;
    count++;
    const slot = byAddr[addr] || (byAddr[addr] = { dogs: null, frequency: "", deoFreq: "" });
    const plan = parsePlanCode(subsName);
    if (plan) { if (plan.dogs != null) slot.dogs = plan.dogs; if (plan.frequency) slot.frequency = plan.frequency; }
    else { const d = deoFromSubsName(subsName); if (d) slot.deoFreq = d; }
  }
  return { byAddr, count };
}

// ── the join ─────────────────────────────────────────────────────────────────
// files: [{ type, text }] where type ∈ {contacts, subscriptions, unknown}.
export function joinImport(files) {
  const contactRows = []; let subsByAddr = {}; let subsCount = 0, contactFiles = 0, subsFiles = 0;
  for (const f of files || []) {
    if (f.type === "subscriptions") {
      subsFiles++;
      const { byAddr, count } = parseSubsCSV(f.text); subsCount += count;
      for (const [a, s] of Object.entries(byAddr)) {
        const cur = subsByAddr[a] || (subsByAddr[a] = { dogs: null, frequency: "", deoFreq: "" });
        if (s.dogs != null) cur.dogs = s.dogs;
        if (s.frequency) cur.frequency = s.frequency;
        if (s.deoFreq) cur.deoFreq = s.deoFreq;
      }
    } else { // contacts OR unknown → try to read as contacts (combined files land here)
      contactFiles++;
      contactRows.push(...parseContactCSV(f.text));
    }
  }
  const contactAddrs = new Set(contactRows.map((c) => c._addr));
  const clients = contactRows.map((c) => {
    const s = subsByAddr[c._addr];
    const dogs = c.dogs != null ? c.dogs : (s ? s.dogs : null);
    const frequency = c.frequency || (s ? s.frequency : "") || "";
    const deoFreq = c.deoFreq || (s ? s.deoFreq : "") || "";
    return { ...c, dogs, frequency, deoFreq, _hasPlan: dogs != null && !!frequency, _matchedSub: !!s };
  });
  const orphanSubs = Object.keys(subsByAddr).filter((a) => !contactAddrs.has(a)).length;
  return {
    clients,
    stats: {
      contacts: contactRows.length, subs: subsCount, contactFiles, subsFiles,
      matched: clients.filter((c) => c._matchedSub).length,
      noPlan: clients.filter((c) => !c._hasPlan).length,
      orphanSubs,
    },
  };
}
